import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";
import { saveAs } from "file-saver";
import cloneDeep from "lodash/cloneDeep";
import merge from "lodash/merge";
import ru from "apexcharts/dist/locales/ru.json";
import numeral from "numeral";
import { loadavg } from "os";
interface MarginalityReportState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  filter: {
    dZoneGroups: any;
    targetDates: Date[];
  };

  items: any[];
  payments: any[];
  total: any;
  accepts: any[];

  graphicList: any[];
}

const state: MarginalityReportState = {
  loading: false,
  loaded: false,
  fatalError: false,

  filter: {
    dZoneGroups: null,
    targetDates: [new Date(), new Date()]
  },

  items: [],
  payments: [],
  total: {},
  accepts: [],

  graphicList: []
};

function buildFilter(params: any) {
  const queryParams: any = {};

  if (params.targetDates) {
    queryParams.date_from = moment(params.targetDates[0]).format("YYYY-MM-DD");
    queryParams.date_to = moment(params.targetDates[1]).format("YYYY-MM-DD");
  }

  if (params.dZoneGroups) {
    queryParams.delivery_zone_group_id = params.dZoneGroups;
  }

  if (params.employees) {
    queryParams.manager_id = params.employees;
  }

  if (params.clients) {
    if (params.clients && !Array.isArray(params.clients)) {
      params.clients = [params.clients];
    }
    params.clients = params.clients.filter((item: any) => item != null);
    queryParams.client_ids = Array.isArray(params.clients)
      ? params.clients
      : [params.clients];
  }

  if (params.sortField !== null) {
    queryParams.order_by = params.sortField || "client_name";
    queryParams.order_type = params.sortDirection || "asc";
  }

  queryParams.no_group = params.no_group ? params.no_group : "";

  return queryParams;
}

export const REPORT_DATA = "REPORT_DATA";
export const ACCEPTS_LIST = "GET_ACCEPTS_LIST";
export const PAYMENTS_LIST = "GET_PAYMENTS_LIST";

export const actions: ActionTree<MarginalityReportState, RootState> = {
  loadData({ commit, state, dispatch }, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      console.log("Object.values(params).length", Object.values(params).length);
      if (Object.values(params).length > 0) {
        commit("START_LOADING");

        vm.$db([
          vm
            .$q(REPORT_DATA)
            .raw("reports.margin")
            .action(buildFilter(params)),
          vm
            .$q(ACCEPTS_LIST)
            .raw("documents.list")
            .action({
              columns: {
                is_accepted: "tcost.is_accepted",
                month: "tcost.month"
              },
              from: "s:transport_costs_accepts",
              from_as: "tcost",
              page_size: 12,
              where: {
                $gte: ["tcost.month", { $: moment().format("YYYY-01-01") }]
              }
            }),
          vm
            .$q(PAYMENTS_LIST)
            .raw("documents.list")
            .action({
              columns: {
                id: "p.id",
                client_id: "c.id",
                client_name: "c.name",
                datetime: "p.datetime",
                type: "p.type",
                payment: "p.payment"
              },
              from: "s:payments",
              from_as: "p",
              joins: [
                {
                  join: "s:clients",
                  as: "c",
                  on: {
                    $eq: ["c.id", "p.client_id"]
                  }
                }
              ],
              page_size: 10000,
              where: {
                $and: [
                  {
                    $gte: [
                      "p.datetime",
                      { $: moment(params.targetDates[0]).format("YYYY-MM-DD") }
                    ]
                  },
                  {
                    $lte: [
                      "p.datetime",
                      { $: moment(params.targetDates[1]).format("YYYY-MM-DD") }
                    ]
                  }
                ]
              }
            })
        ]).then((result: ServerResult) => {
          commit("SET_ACCEPTS", result.getOperation(ACCEPTS_LIST).getContent());
          commit(
            "SET_REPORT_DATA",
            result.getOperation(REPORT_DATA).getContent()
          );
          commit(
            "SET_PAYMENTS_DATA",
            result.getOperation(PAYMENTS_LIST).getContent()
          );
          commit("LOADED_SUCCESS");

          resolve();
        });
      } else {
        resolve();
      }
    });
  },

  exportDataAsXLSX({ rootState, commit }, params) {
    const vm = (this as any)._vm;
    commit("START_LOADING");

    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("reports.margin.export")
          .action({ ...buildFilter(params), format: "xlsx" })
      ]).then((result: ServerResult) => {
        const exportOpContent = result.getOperation("EXPORT").getContent();
        const fToken = exportOpContent.token;
        const aToken = (rootState as any).ubic.token;
        const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${fToken}?authorization=${aToken}`;
        saveAs(fileUrl, "export.xlsx");
        commit("LOADED_SUCCESS");

        resolve();
      });
    });
  },

  removeItem({ state, dispatch }, row) {
    const vm = (this as any)._vm;
    return new Promise((resolve, reject) => {
      vm.$db([
        vm
          .$q("EXPORT")
          .raw("documents.delete")
          .action({ id: row.id })
      ]).then((result: ServerResult) => {
        resolve(null);
      });
    });
  },

  removeItems({ state, dispatch }, rows) {
    const vm = (this as any)._vm;
    return new Promise((resolve, reject) => {
      const actions = rows.map((item: any) => {
        return vm
          .$q(`RM-${item.id}`)
          .raw("documents.delete")
          .action({ id: item.id });
      });
      vm.$db(actions).then((result: ServerResult) => {
        resolve(null);
      });
    });
  }
};

export const mutations: MutationTree<MarginalityReportState> = {
  START_LOADING: state => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },

  SET_ACCEPTS(state, data) {
    state.accepts = data.documents;
  },

  SET_PAYMENTS_DATA(state, data) {
    const client_ids: any[] = state.items.map((item: any) => {
      return item.client_id;
    });
    state.payments = data.documents.map((item: any) => {
      item.connected = client_ids.includes(item.client_id);
      return item;
    });
  },

  SET_REPORT_DATA(state, data) {
    state.items = data.documents;
    state.total = data.sums;

    const baseSettings = {
      chart: {
        locales: [ru],
        defaultLocale: "ru",
        height: 250,
        toolbar: {
          show: false,
          tools: {
            selection: false
          }
        },
        selection: {
          enabled: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        curve: "straight"
      },
      markers: {
        size: 6,
        hover: {
          size: 10
        }
      },
      tooltip: {
        // shared: false,
        followCursor: false,
        theme: "dark",
        x: {
          show: false
        },
        marker: {
          show: false
        },
        y: {
          formatter: (
            value: any,
            { series, seriesIndex, dataPointIndex, w }: any
          ) => {
            return numeral(value).format("0,0.00");
          },
          title: {
            formatter: (serieLabel: any, data: any) => {
              return "";
            }
          }
        }
      },
      grid: {
        clipMarkers: false
      },
      yaxis: {
        tickAmount: 2
      },
      xaxis: {
        type: "datetime"
      }
    };

    const graphicList = [
      merge(cloneDeep(baseSettings), {
        title: {
          text: "Объем отгрузок, кг",
          align: "left"
        },
        chart: {
          id: "weightChart",
          group: "social",
          type: "line"
        },
        colors: ["#008FFB"],
        series: [
          {
            data: data.date_stats.map((row: any) => {
              return [
                moment(row.date, "YYYY-MM-DD").valueOf(),
                parseFloat(row.weight)
              ];
            })
          }
        ],
        yaxis: {
          labels: {
            minWidth: 0
          }
        }
      }),
      merge(cloneDeep(baseSettings), {
        title: {
          text: "Выручка / Выручка-себестоимость-транспортные",
          align: "left"
        },
        chart: {
          id: "primeCostChart",
          group: "social",
          type: "line"
        },
        colors: ["#00E396", "#546E7A"],
        series: [
          {
            name: "Выручка, руб",
            data: data.date_stats.map((row: any) => {
              return [
                moment(row.date, "YYYY-MM-DD").valueOf(),
                parseFloat(row.income)
              ];
            })
          },
          {
            name: "Выручка-себестоимость-транспортные, руб",
            data: data.date_stats.map((row: any) => {
              return [
                moment(row.date, "YYYY-MM-DD").valueOf(),
                parseFloat(row.profit_amount)
              ];
            })
          }
        ],
        yaxis: {
          labels: {
            minWidth: 0
          }
        }
      })
    ];

    state.graphicList = graphicList;
  }
};

const getters = {
  byMonthy: (state: MarginalityReportState) => {
    const accepts: any = {
      0: { month: moment().format("YYYY-01-01") },
      1: { month: moment().format("YYYY-02-01") },
      2: { month: moment().format("YYYY-03-01") },
      3: { month: moment().format("YYYY-04-01") },
      4: { month: moment().format("YYYY-05-01") },
      5: { month: moment().format("YYYY-06-01") },
      6: { month: moment().format("YYYY-07-01") },
      7: { month: moment().format("YYYY-08-01") },
      8: { month: moment().format("YYYY-09-01") },
      9: { month: moment().format("YYYY-10-01") },
      10: { month: moment().format("YYYY-11-01") },
      11: { month: moment().format("YYYY-12-01") }
    };

    state.accepts.map((item: any) => {
      accepts[parseInt(moment(item.month).format("MM")) - 1] = item;
    });

    return accepts;
  },

  totalPayments: (state: MarginalityReportState) => {
    return state.payments.reduce((val: any, item: any) => {
      // console.log(item)
      return val + parseFloat(item.payment);
    }, 0);
  }
};

export const STORE_KEY = "$_marginality_report";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
